import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2 } from "lucide-react";

export function WaitlistDialog({
  open, onOpenChange, plan, source = "pricing", kind = "waitlist",
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  plan?: string;
  source?: string;
  kind?: "waitlist" | "newsletter";
}) {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const r = await fetch("/api/public/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          plan,
          source,
          kind,
          website,
          locale: i18n.resolvedLanguage?.startsWith("en") ? "en" : "it",
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || t("waitlist.error"));
      setDone(true);
    } catch (e: any) {
      setError(e?.message ?? t("waitlist.error"));
    } finally {
      setLoading(false);
    }
  };

  const desc = kind === "waitlist"
    ? (plan
      ? (i18n.resolvedLanguage?.startsWith("en")
        ? `Payments are coming soon for the ${plan} plan. Leave your email: early subscribers get an early-bird discount.`
        : `I pagamenti sono in attivazione per il piano ${plan}. Lascia la tua email: i primi iscritti riceveranno uno sconto early-bird dedicato.`)
      : (i18n.resolvedLanguage?.startsWith("en")
        ? "Payments are coming soon. Leave your email: early subscribers get an early-bird discount."
        : "I pagamenti sono in attivazione. Lascia la tua email: i primi iscritti riceveranno uno sconto early-bird dedicato."))
    : t("waitlist.descNewsletter");

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setDone(false); setEmail(""); setError(null); } }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">
            {kind === "waitlist" ? t("waitlist.titleWaitlist") : t("waitlist.titleNewsletter")}
          </DialogTitle>
          <DialogDescription>{desc}</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <CheckCircle2 className="h-10 w-10 text-success" />
            <p className="text-sm">{t("waitlist.success")}</p>
            <Button onClick={() => onOpenChange(false)} variant="outline">{t("waitlist.close")}</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="wl-email">{t("waitlist.emailLabel")}</Label>
              <Input id="wl-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("waitlist.emailPlaceholder")} />
            </div>
            <input type="text" name="website" value={website} onChange={(e) => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden />
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full">
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("waitlist.sending")}</> : t("waitlist.subscribe")}
            </Button>
            <p className="text-xs text-muted-foreground">{t("waitlist.consent")}</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
